import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { compareBy, Navigate } from '@ps-gui/shared-utils';
import { CtBatch, CtBatchMetaData, ProofCtBatch } from '@ps-gui/claims-terminal-shared';
import { ToastService } from '@ps-gui/shared-core';

import { buildAuditPatch } from './audit-patch.builder';
import { CtBatchRedirectStoreService } from './ct-batch-redirect-store.service';
import { ExtractionTrainingSubmissionService } from './extraction-training-submission.service';
import { PersonenschadenApiService } from '../api/personenschaden-api.service';
import { CtBatchExtractionApiService } from '../api/ct-batch-extraction-api.service';
import { CtBatchStoreService } from '../app-state/ct-batch-store.service';
import { PersistDataService } from '../app-state/persist-data.service';
import { ExtractedDataSnapshot } from '../../models/extracted-values';
import { SplitEntity } from '../../models/interfaces';
import { countForderungsaufstellungen } from '../utils';

/**
 * Handles transmission for `ProofCtBatch` (Audit-Pfad).
 */
@Injectable({ providedIn: 'root' })
export class AuditCtBatchTransmissionService {
  private readonly personenschadenApiService = inject(PersonenschadenApiService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly toastService = inject(ToastService);
  private readonly trainingSubmission = inject(ExtractionTrainingSubmissionService);
  private readonly redirectStore = inject(CtBatchRedirectStoreService);

  transmitAndRedirect(ctBatch: ProofCtBatch): Observable<unknown> {
    return this.finalize(ctBatch, ctBatch.auditId);
  }

  /**
   * Shared finalize block: split → persist split entities → submit training data →
   * process$ per split id → signal Ct2 completion → reset persisted state → redirect.
   */
  finalize(ctBatch: CtBatch, auditId: string): Observable<void> {
    return this.splitProofByCtBatch(ctBatch, auditId).pipe(
      map((splitEntities) =>
        this.persistDataService.setSplitEntities([
          { splitIndex: -1, copyId: auditId },
          ...splitEntities,
        ]),
      ),
      switchMap(() => this.trainingSubmission.submitTrainingDataForDocuments()),
      switchMap(() => {
        const auditIds = this.getAllRelatedIds();
        return forkJoin(
          auditIds.map((id, index) => this.sendDataToPersonenschadenApi(ctBatch, id, index)),
        );
      }),
      switchMap(() => this.ctBatchExtractionApiService.signalCtBatchCt2Completion(ctBatch.id)),
      catchError((error: unknown) => {
        this.toastService.addErrorToast('Fehler beim Abschließen des Extraktionsvorgangs.');
        return throwError(() => error);
      }),
      switchMap(() => from(this.persistDataService.reset())),
      map(() => this.browserRedirect()),
    );
  }

  browserRedirect() {
    Navigate.replace(this.redirectStore.getRedirectUrl(this.ctBatchStoreService.ctBatch));
  }

  private splitProofByCtBatch(ctBatch: CtBatch, auditId: string): Observable<SplitEntity[]> {
    const numberOfForderungsaufstellungen = countForderungsaufstellungen(ctBatch);
    if (numberOfForderungsaufstellungen <= 1) return of([]);
    return this.personenschadenApiService.splitProof(auditId, numberOfForderungsaufstellungen - 1);
  }

  /**
   * Returns an array of audit ids where the first element is the id of the audit
   * associated with the current ct batch, followed by all copy ids from created splits.
   */
  private getAllRelatedIds() {
    return this.persistDataService
      .getSplitEntities()
      .toSorted(compareBy((split) => split.splitIndex))
      .map((split) => split.copyId);
  }

  private sendDataToPersonenschadenApi(
    ctBatch: CtBatch,
    auditId: string,
    splitIndex: number,
  ): Observable<string> {
    return from(this.persistDataService.getConsolidatedDataSnapshot(splitIndex)).pipe(
      switchMap((snapshot) =>
        this.enrichAudit(auditId, snapshot, ctBatch.metaData).pipe(
          tap((auditId) => this.redirectStore.setAuditRedirectUrl(ctBatch.id, auditId)),
        ),
      ),
    );
  }

  private enrichAudit(
    auditId: string,
    snapshot: ExtractedDataSnapshot,
    metaData?: CtBatchMetaData,
  ): Observable<string> {
    return from(
      buildAuditPatch(
        snapshot,
        this.ctBatchStoreService.ctBatchDomain,
        this.ctBatchStoreService.ctBatch.documents,
        this.persistDataService,
        metaData,
        auditId,
      ),
    ).pipe(
      switchMap((auditPatch) => this.personenschadenApiService.patchAudit(auditPatch, auditId)),
      map((_) => auditId),
    );
  }
}
